import type { Dataset } from "@/models/Dataset";
import type { Client } from "@/models/Dashboards";

export interface DatasetGroup {
  label: string;
  datasets: Dataset[];
}

/**
 * Organises all datasets into up to 4 priority groups:
 *  1. In Use in This Dashboard  (inUseIds)
 *  2. Primary Client ([name])   (dataset.client.id === primaryClient.id)
 *  3. Additional Clients        (dataset.client.id in additionalClients[].id)
 *  4. Other Datasets            (everything else)
 *
 * Within each group datasets are sorted alphabetically by name.
 * Empty groups are omitted from the result.
 */
export function groupDatasets(
  allDatasets: Dataset[],
  inUseIds: string[],
  primaryClient?: Client,
  additionalClients: Client[] = [],
): DatasetGroup[] {
  const inUseSet = new Set(inUseIds);
  const additionalClientIds = new Set(additionalClients.map((c) => c.id));

  const alpha = (a: Dataset, b: Dataset) => a.name.localeCompare(b.name);

  const inUse: Dataset[] = [];
  const primary: Dataset[] = [];
  const additional: Dataset[] = [];
  const other: Dataset[] = [];

  for (const dataset of allDatasets) {
    const clientId = dataset.client?.id;

    if (inUseSet.has(dataset.id)) {
      inUse.push(dataset);
    } else if (primaryClient && clientId === primaryClient.id) {
      primary.push(dataset);
    } else if (clientId && additionalClientIds.has(clientId)) {
      additional.push(dataset);
    } else {
      other.push(dataset);
    }
  }

  inUse.sort(alpha);
  primary.sort(alpha);
  additional.sort(alpha);
  other.sort(alpha);

  const groups: DatasetGroup[] = [];

  if (inUse.length > 0) {
    groups.push({ label: "In Use in This Dashboard", datasets: inUse });
  }
  if (primary.length > 0 && primaryClient) {
    groups.push({ label: `Primary Client (${primaryClient.name})`, datasets: primary });
  }
  if (additional.length > 0) {
    groups.push({ label: "Additional Clients", datasets: additional });
  }
  if (other.length > 0) {
    groups.push({ label: "Other Datasets", datasets: other });
  }

  return groups;
}
